import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router';

import { AppRouteKeys, getRoutePath } from '~/core/router/router_utils';
import { AppRoutes } from '~/core/router/routes';
import { useNavigationStore } from '~/core/stores/navigation_store';
import { useNavigation } from '~/core/utils/navigation';
import { useLayoutStore } from '~/shared/stores/layoutStore';

type SecureLinkProps = {
  to: AppRouteKeys;
  params?: Record<string, string>;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
};

/**
 * A simplified secure link component that relies on server-side validation.
 * The middleware enforces all permission checks on the navigation's own
 * loader request (route_middleware.server.ts -> validateTransition) using
 * the X-Client-Navigation headers goTo()/navigate() already attach, so no
 * separate pre-flight request is needed before starting the real
 * navigation — it would only be re-asking the server the same question the
 * real request is about to ask anyway, one network round-trip later.
 */
export function SecureLink({ to, params, className, children, onClick, href }: SecureLinkProps) {
  const { goTo } = useNavigation();
  const navigate = useNavigate();
  const location = useRouterLocation();
  const setPendingNavigation = useLayoutStore((state) => state.setPendingNavigation);

  // Clear isPendingNavigation when the route actually changes
  useEffect(() => {
    setPendingNavigation(false);
  }, [location.key]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setPendingNavigation(true);

    const { currentRoute } = useNavigationStore.getState();
    console.info(`🔗 [SecureLink] Navigating from ${currentRoute || 'UNKNOWN'} to ${to}`);

    if (onClick) {
      onClick(e);
    }

    const targetPath = href || (params ? getRoutePath(to, params) : AppRoutes[to].path);

    // Server-side middleware redirects to /no-access on this request itself
    // if the transition isn't allowed, just like the removed pre-flight did.
    if (params || href) {
      navigate(targetPath);
    } else {
      goTo(to);
    }
  };

  const targetPath = href || (params ? getRoutePath(to, params) : AppRoutes[to].path);

  return (
    <Link
      to={targetPath}
      className={className}
      onClick={handleClick}
      data-testid={`secure-link-${to}`}
      data-route-key={to}
      preventScrollReset={false}
    >
      {children}
    </Link>
  );
}
